// Which accounts a person may book a category change to (rd15 owner decisions
// 5 and 6, 2026-11-02).
//
// WHY THIS IS ITS OWN MODULE. The Expense audit page has had this rule since
// the audit design; round 15 put the same "Change category" control in a
// transaction's detail (`transactions.ts`), which imported it from the page.
// A page importing another page is the shape that drifts when one of them is
// edited in isolation, and the owner widened the rule this round ("Income
// categories can be changed too; equity, loans, transfers stay locked"), so the
// two surfaces now read one file.
//
// LOCKSTEP. This is the console's copy of the Worker's
// `expenseAuditAccountAllowed` (`ingest-worker/src/expense_audit.ts`), which is
// itself in lockstep with `pipeline/expense_audit.py#_allowed_audit_account`.
// The Worker is the authority: it refuses a decision line this returns true for
// only when the account is not open to the company (a fact the console reads
// from the chart snapshot). Everything below is the Worker's test, verbatim, so
// the console never offers a category the answer route would reject.
//
// WHAT IT REFUSES, AND WHY.
//   - Cash and bank accounts: a reclass is cash-neutral, so the cash side is
//     never the category being chosen.
//   - Equity: opening equity is generated by the run and feeds the opening
//     balances; a person does not choose it.
//   - Liabilities, loans and receivables: these carry a schedule or a
//     counterparty the run keeps, not a category.
//   - Intercompany (`Due-From`/`Due-To`) and anything named as a draw, a
//     shareholder or a member loan: a transfer is generated as a pair
//     (invariant 7.13) and has no category a person chose.
//   - A path that names none of these but sits under no allowed root.
//
// INCOME IS ALLOWED. Owner decision 6: "Income categories can be changed too".
// The pipeline indexes an income entry as eligible for the same reason, so the
// audit read a detail page makes does return income lines. Equity, loans,
// transfers and cash stay locked.

/** The Worker's intercompany pair pattern, lockstep with `pipeline/config.py`. */
const INTERCOMPANY_ACCOUNT =
  /^(Assets|Liabilities):([A-Z][A-Z0-9]*):(Due-From|Due-To):([A-Z][A-Z0-9]*)$/;

/** The Worker's judgment-name rule: a draw, a shareholder, a member loan. */
const JUDGMENT_ACCOUNT =
  /due\s+to|due\s+from|owners?\s+draw|owner's\s+draw|shareholder|intercompany|member\s+draw|member\s+loan/i;

/** Segments any of which makes an `Assets:` account a cash account. */
const CASH_SEGMENT = new Set(["Cash", "Checking", "Savings", "Operating", "Bank"]);

/** True when an account may be the category of a change a person makes. */
export function auditAccountAllowed(account: string): boolean {
  const parts = account.split(":");
  const root = parts[0];
  if (root !== "Expenses" && root !== "Income" && root !== "Assets") return false;
  if (INTERCOMPANY_ACCOUNT.test(account)) return false;
  if (JUDGMENT_ACCOUNT.test(account)) return false;
  if (parts.includes("Loans") || parts.includes("Receivable") || parts.includes("Receivables")) {
    return false;
  }
  if (root === "Assets") {
    const tail = parts.slice(2);
    if (tail.some((part) => CASH_SEGMENT.has(part))) return false;
  }
  return true;
}
